/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import AddIcon from '@mui/icons-material/Add';

import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import { tableV2StackedCellContentSx } from '../../../components/v2/tableV2Styles';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import { deleteProduto } from '../../../store/produto/actions';
import { PRODUTO_PEDIDO_DELETE_ACCESS } from '../../../constants/modules';
import { hasModuleConfiguration } from '../../../helpers/moduleConfigurationPermission';
import { codeFormatter } from 'utils';
import ProdutoFormulario from './ProdutoFormulario';

const hasPermission = (moduleConfigurations, authData) =>
  hasModuleConfiguration(moduleConfigurations, authData, PRODUTO_PEDIDO_DELETE_ACCESS);

const getIdFromSearch = (search) => {
  if (!search) return null;
  const id = new URLSearchParams(search).get('id');
  return id && String(id).trim() ? String(id).trim() : null;
};

const getTabFromSearch = (search) => {
  if (!search) return null;
  const tab = new URLSearchParams(search).get('tab');
  return tab === 'extrato' ? 'extrato' : null;
};

const getLocalFromSearch = (search) => {
  if (!search) return '';
  const local = new URLSearchParams(search).get('local');
  return local && String(local).trim() ? String(local).trim() : '';
};

const EstoqueProdutosV2 = ({
  deleteProduto,
  authData,
  moduleConfigurations,
  history,
  location,
}) => {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [localFilter, setLocalFilter] = useState({ codigo: undefined });
  const [reload, setReload] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [forceDeleteOpen, setForceDeleteOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [conflictMessage, setConflictMessage] = useState('');
  const [initialTab, setInitialTab] = useState(null);
  const [initialLocal, setInitialLocal] = useState('');
  const tableReloadRef = useRef(() => {});

  const canDelete = hasPermission(moduleConfigurations, authData);

  useEffect(() => {
    const deepLinkId = getIdFromSearch(location?.search);
    if (!deepLinkId) return;
    setId(deepLinkId);
    setInitialTab(getTabFromSearch(location?.search));
    setInitialLocal(getLocalFromSearch(location?.search));
    setOpen(true);
  }, [location?.search]);

  const replaceSearch = (mutate) => {
    const params = new URLSearchParams(location.search);
    mutate(params);
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  };

  const setProdutoIdInUrl = (produtoId, { clearTabAndLocal = false } = {}) => {
    replaceSearch((params) => {
      params.set('id', String(produtoId));
      if (clearTabAndLocal) {
        params.delete('tab');
        params.delete('local');
      }
    });
  };

  const clearDeepLinkId = () => {
    if (!getIdFromSearch(location?.search) && !getTabFromSearch(location?.search)) return;
    replaceSearch((params) => {
      params.delete('id');
      params.delete('tab');
      params.delete('local');
    });
  };

  const onEdit = rowId => {
    setOpen(true);
    setId(rowId);
    setInitialTab(null);
    setInitialLocal('');
    setProdutoIdInUrl(rowId, { clearTabAndLocal: true });
  };

  const onAdd = () => {
    setOpen(true);
    setId(null);
    setInitialTab(null);
    setInitialLocal('');
  };

  const onDelete = (rowId, reload) => {
    tableReloadRef.current = reload;
    deleteProduto(rowId, reload, {
      onVinculos409: msg => {
        setConflictMessage(msg || '');
        setPendingDeleteId(rowId);
        setForceDeleteOpen(true);
      },
    });
  };

  const handleForceDeleteClose = () => {
    setForceDeleteOpen(false);
    setPendingDeleteId(null);
    setConflictMessage('');
  };

  const handleForceDeleteConfirm = () => {
    deleteProduto(
      pendingDeleteId,
      () => {
        handleForceDeleteClose();
        tableReloadRef.current();
      },
      { force: true }
    );
  };

  const onClose = isSave => {
    setOpen(false);
    setId(null);
    setInitialTab(null);
    setInitialLocal('');
    clearDeepLinkId();
    if (isSave) {
      setReload(current => !current);
    }
  };

  const onPersisted = produto => {
    const nextId = produto?.id != null ? String(produto.id) : null;
    if (nextId) setId(nextId);
    setReload(current => !current);
    if (nextId) {
      setProdutoIdInUrl(nextId);
    }
  };

  const headerActions = [
    {
      label: 'Novo',
      onClick: onAdd,
      startIcon: <AddIcon />,
      variant: 'contained',
    },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Typography variant="h4">Produtos</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="produtos/filter"
          filterData={localFilter}
          refreshKey={reload}
          rowHeight={56}
          headerActions={headerActions}
          columns={[
            {
              field: 'nome',
              headerName: 'Produto',
              flex: 1,
              minWidth: 200,
              renderCell: ({ row }) => (
                <Box sx={tableV2StackedCellContentSx}>
                  <Typography variant="body2">{row.nome}</Typography>
                  {row.codigo != null && row.codigo !== '' && (
                    <Typography variant="body2" color="text.secondary">
                      ({codeFormatter(row.codigo)})
                    </Typography>
                  )}
                </Box>
              ),
            },
            {
              field: 'ativo',
              headerName: 'Ativo',
              width: 100,
              renderCell: ({ row }) =>
                row.ativo === 'true' || row.ativo === true ? 'Sim' : 'Não',
            },
            {
              field: 'estoqueTotal',
              headerName: 'Quantidade',
              width: 120,
              renderCell: ({ row }) => row.estoqueTotal || '0',
            },
            {
              field: 'possuiTamanho',
              headerName: 'Possui Tamanho',
              width: 150,
              renderCell: ({ row }) =>
                row.possuiTamanho === 'true' || row.possuiTamanho === true ? 'Sim' : 'Não',
            },
            {
              field: 'descricao',
              headerName: 'Descrição',
              flex: 1,
              minWidth: 200,
            },
          ]}
          onEdit={onEdit}
          onDelete={onDelete}
          canDelete={() => canDelete}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          appliedFilter={localFilter}
          fields={[
            { field: 'useSelectOption', key: 'ativo', label: 'Ativo', options: [{ id: 'true', nome: 'Sim' }, { id: 'false', nome: 'Não' }], md: 4 },
            { field: 'useProduto', key: 'produtoIds', label: 'Produtos', md: 12, multiSelect: true },
          ]}
          onFilter={filterData => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />

        <ProdutoFormulario
          id={id}
          open={open}
          onClose={onClose}
          onPersisted={onPersisted}
          initialTab={initialTab}
          initialLocal={initialLocal}
        />

        <DialogDefaultV2
          open={forceDeleteOpen}
          handlerCancel={handleForceDeleteClose}
          handlerConfirm={handleForceDeleteConfirm}
          title="Exclusão com vínculos"
          confirmMessage="Excluir mesmo assim"
          cancelMessage="Cancelar"
        >
          <DialogContentText>
            {conflictMessage}{' '}
            Deseja excluir o produto mesmo assim? Esta ação não poderá ser desfeita.
          </DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

EstoqueProdutosV2.propTypes = {
  deleteProduto: PropTypes.func.isRequired,
  authData: PropTypes.object.isRequired,
  moduleConfigurations: PropTypes.array,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  deleteProduto,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EstoqueProdutosV2));
